import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Wallet, TrendingUp, PieChart, ArrowUpRight, LogOut, ShieldCheck, Heart, Tag, Store, Download, FileText, Compass, LineChart } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useTour } from '../context/TourContext';
import { getPortfolio, getSaved, statementUrl } from '../lib/api';
import ProjectCard from '../components/ProjectCard';
import ProjectDetail from '../components/ProjectDetail';
import SellDialog from '../components/SellDialog';
import HoldingDetail from '../components/HoldingDetail';
import PortfolioChart from '../components/PortfolioChart';

const money = (n) => `$${Number(n || 0).toLocaleString()}`;

export default function Dashboard() {
  const { user, logout } = useAuth();
  const { startTour } = useTour();
  const [portfolio, setPortfolio] = useState(null);
  const [saved, setSaved] = useState([]);
  const [selected, setSelected] = useState(null);
  const [sellHolding, setSellHolding] = useState(null);
  const [detailId, setDetailId] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const [p, s] = await Promise.all([getPortfolio(), getSaved()]);
      setPortfolio(p);
      setSaved(s);
    } catch (e) { console.error('Failed to load portfolio data', e); } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const firstName = (user?.name || 'Investor').split(' ')[0];
  const holdings = portfolio?.holdings || [];

  return (
    <div className="min-h-screen bg-farmway-green pt-28">
      <div className="container-fw pb-24">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <span className="eyebrow">Investor portal</span>
            <h1 className="mt-3 display-xl text-4xl text-farmway-cream sm:text-5xl">Welcome back, {firstName}</h1>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <button data-testid="start-tour-btn" onClick={startTour} className="btn-outline"><Compass className="h-4 w-4" /> Tour</button>
            <Link to="/reports" data-tour="reports-link" data-testid="reports-link" className="btn-outline"><FileText className="h-4 w-4" /> Reports</Link>
            <Link to="/marketplace" className="btn-gold"><Store className="h-4 w-4" /> Marketplace</Link>
            {user?.role === 'admin' && <Link to="/admin" className="btn-outline"><ShieldCheck className="h-4 w-4" /> Admin</Link>}
            <button onClick={logout} className="btn-outline"><LogOut className="h-4 w-4" /> Sign out</button>
          </div>
        </div>

        {/* portfolio summary */}
        <div className="mt-10">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <h2 className="font-display text-xl font-bold text-farmway-cream">Portfolio</h2>
            {holdings.length > 0 && (
              <a href={statementUrl()} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 rounded-full border border-white/15 px-4 py-2 font-display text-xs font-bold uppercase tracking-wider text-farmway-cream/80 transition-colors hover:border-farmway-gold hover:text-farmway-gold">
                <Download className="h-4 w-4" /> Download statement
              </a>
            )}
          </div>
          <div data-tour="portfolio-totals" className="mt-5 grid gap-5 sm:grid-cols-3">
            <div className="rounded-2xl border border-white/10 bg-farmway-green-mid/40 p-6">
              <Wallet className="h-6 w-6 text-farmway-gold" />
              <p className="mt-4 font-display text-3xl font-black text-farmway-cream">{money(portfolio?.totalInvested)}</p>
              <p className="mt-1 text-xs text-farmway-cream/55">Total invested</p>
            </div>
            <div className="rounded-2xl border border-white/10 bg-farmway-green-mid/40 p-6">
              <PieChart className="h-6 w-6 text-farmway-gold" />
              <p className="mt-4 font-display text-3xl font-black text-farmway-cream">{money(portfolio?.currentValue)}</p>
              <p className="mt-1 text-xs text-farmway-cream/55">Current value</p>
            </div>
            <div className="rounded-2xl border border-white/10 bg-farmway-gold/10 p-6">
              <TrendingUp className="h-6 w-6 text-farmway-gold" />
              <p className="mt-4 font-display text-3xl font-black text-farmway-gold">{(portfolio?.totalReturnPct ?? 0) >= 0 ? '+' : ''}{portfolio?.totalReturnPct ?? 0}%</p>
              <p className="mt-1 text-xs text-farmway-cream/55">Total return</p>
            </div>
          </div>

          {portfolio?.valueHistory?.length > 1 && (
            <div data-tour="portfolio-chart" className="mt-6 rounded-2xl border border-white/10 bg-farmway-green-mid/40 p-6">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <p className="flex items-center gap-2 font-display text-sm font-bold uppercase tracking-wider text-farmway-cream/70">
                  <LineChart className="h-4 w-4 text-farmway-gold" /> Portfolio performance
                </p>
                <p className="text-xs text-farmway-cream/50">Combined value of all holdings, last {portfolio.valueHistory.length} months</p>
              </div>
              <div className="mt-4">
                <PortfolioChart points={portfolio.valueHistory} />
              </div>
            </div>
          )}

          {loading ? (
            <p className="mt-6 text-farmway-cream/50">Loading…</p>
          ) : holdings.length > 0 ? (
            <div data-tour="holdings-table" className="mt-6 overflow-hidden rounded-2xl border border-white/10">
              <div className="hidden grid-cols-6 gap-4 bg-farmway-green-mid/40 px-6 py-3 text-[11px] uppercase tracking-wider text-farmway-cream/50 sm:grid">
                <span className="col-span-2">Farm</span><span>Units</span><span>Invested</span><span>Current</span><span className="text-right">Action</span>
              </div>
              {holdings.map((h) => (
                <div key={h.holdingId} className="grid grid-cols-2 items-center gap-4 border-t border-white/10 px-6 py-4 sm:grid-cols-6">
                  <div className="col-span-2 flex items-center gap-3">
                    {h.project?.image && <img src={h.project.image} alt="" className="h-10 w-10 rounded-lg object-cover" />}
                    <div>
                      <button data-testid={`holding-open-${h.holdingId}`} onClick={() => setDetailId(h.holdingId)} className="text-left text-sm font-medium text-farmway-cream underline-offset-4 hover:text-farmway-gold hover:underline">{h.project?.name}</button>
                      <p className="font-display text-xs font-bold text-farmway-leaf-bright">{h.returnPct >= 0 ? '+' : ''}{h.returnPct}%</p>
                    </div>
                  </div>
                  <p className="text-sm text-farmway-cream/80">{h.units}</p>
                  <p className="text-sm text-farmway-cream/80">{money(h.invested)}</p>
                  <p className="text-sm text-farmway-cream/80">{money(h.currentValue)}</p>
                  <div className="text-right">
                    {h.listed ? (
                      <span className="rounded-full bg-white/10 px-3 py-1 text-[11px] font-bold uppercase tracking-wider text-farmway-cream/60">Listed</span>
                    ) : (
                      <button data-testid={`holding-sell-${h.holdingId}`} onClick={() => setSellHolding({ holdingId: h.holdingId, currentValue: h.currentValue, name: h.project?.name })} className="inline-flex items-center gap-1 rounded-full border border-farmway-gold/60 px-3 py-1 text-[11px] font-bold uppercase tracking-wider text-farmway-gold hover:bg-farmway-gold hover:text-farmway-green transition-colors"><Tag className="h-3 w-3" />Sell</button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="mt-6 rounded-2xl border border-dashed border-white/15 bg-farmway-green-mid/20 p-12 text-center">
              <p className="text-farmway-cream/60">No investments recorded yet. Once Farmway adds your holdings, they&apos;ll appear here.</p>
            </div>
          )}
        </div>

        {/* saved projects */}
        <div className="mt-14">
          <div className="flex items-center justify-between">
            <h2 className="flex items-center gap-2 font-display text-xl font-bold text-farmway-cream"><Heart className="h-5 w-5 text-farmway-gold" /> Saved projects</h2>
            <Link to="/investment-opportunities" className="inline-flex items-center gap-2 font-display text-sm font-bold uppercase tracking-wider text-farmway-gold hover:gap-3 transition-all">Browse all <ArrowUpRight className="h-4 w-4" /></Link>
          </div>
          {loading ? <p className="mt-6 text-farmway-cream/50">Loading…</p> : saved.length > 0 ? (
            <div className="mt-6 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {saved.map((p) => <ProjectCard key={p.id} project={p} onOpen={setSelected} />)}
            </div>
          ) : (
            <div className="mt-6 rounded-2xl border border-dashed border-white/15 bg-farmway-green-mid/20 p-12 text-center">
              <p className="text-farmway-cream/60">You haven&apos;t saved any projects yet.</p>
              <Link to="/investment-opportunities" className="btn-gold mx-auto mt-6">Explore opportunities <ArrowUpRight className="h-4 w-4" /></Link>
            </div>
          )}
        </div>
      </div>
      <ProjectDetail project={selected} onClose={() => setSelected(null)} />
      <SellDialog holding={sellHolding} open={Boolean(sellHolding)} onOpenChange={(o) => !o && setSellHolding(null)} onListed={load} />
      <HoldingDetail holdingId={detailId} open={Boolean(detailId)} onOpenChange={(o) => !o && setDetailId(null)} />
    </div>
  );
}
